import { FIELD_TYPES } from 'utility/constants';
import { useCamelCaseActivityQuery } from 'queries/models/activites';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast, toastVariant } from 'components/ToastProvider';
import ActivityService from 'services/ActivityService';
import { getOriginalError } from 'services/AxiosService';
import {
  getContactSectionAccess,
  getCustomObjectEntitiesAccessById,
} from 'store/authentication/selectors';
import { EMPTY_OBJECT, isContact } from 'utility/fieldHelpers';

export const EMPTY_ARRAY = [];

export const ASSIGNMENT_INIT_CLIENT = 'ASSIGNMENT_INIT_CLIENT';
export const ASSIGNMENT_SET_CLIENT = 'ASSIGNMENT_SET_CLIENT';
export const ASSIGNMENT_INIT = 'ASSIGNMENT_INIT';
export const ASSIGNMENT_SET = 'ASSIGNMENT_SET';
export const ASSIGNMENT_SET_MULTIPLE = 'ASSIGNMENT_SET_MULTIPLE';
export const CONTACT_SELECTOR = 'CONTACT_SELECTOR';
export const CUSTOM_SELECTOR = 'CUSTOM_SELECTOR';

export const assignmentReducer = (state, action) => {
  switch (action.type) {
    case ASSIGNMENT_INIT_CLIENT:
      return { ...state, client_client: null };

    case ASSIGNMENT_SET_CLIENT:
      return { ...state, client_client: action.value };

    case ASSIGNMENT_INIT:
      return { ...state, [action.name]: null };

    case ASSIGNMENT_SET:
      return { ...state, [action.name]: action.value };

    case ASSIGNMENT_SET_MULTIPLE:
      return { ...state, ...action.value };

    default:
      throw new Error();
  }
};
export const dummyObject = {
  access: {
    edit: true,
    view: true,
  },
};

// Simple helper function to use an ever-updating ref
const useLatest = (val) => {
  const ref = useRef(val);
  ref.current = val;
  return ref;
};

export const useGetRelationshipsForActivity = (
  { client, defaultAssignment, dispatchAssignment }, // predefined options
  { activityObjectId, queryOptions, apiOptions, errorCallback } // activity call options
) => {
  const { currentActivity, isFetching } = useCurrentActivity(
    activityObjectId || null,
    {
      ...queryOptions,
      enabled: !!activityObjectId,
      initialData: EMPTY_OBJECT,
    },
    apiOptions,
    errorCallback
  );
  const authentication = useSelector((s) => s.authentication);
  const authenticationRef = useRef(authentication);
  // Keep track of these values to be used in stable form, whatever the latest value is.
  // We cannot run the `getRelatedActivityFieldWithSideEffects` all the time, otherwise
  // resulting in infinite render loop. We stabilize the incoming values here instead.
  const dispatchAssignmentRef = useLatest(dispatchAssignment);

  const relatedFields = useMemo(() => {
    if (!activityObjectId || isFetching) return EMPTY_ARRAY;
    return getRelatedActivityFieldWithSideEffects(
      client,
      defaultAssignment,
      dispatchAssignmentRef.current,
      currentActivity,
      authenticationRef.current
    );
  }, [
    activityObjectId,
    dispatchAssignmentRef,
    isFetching,
    currentActivity,
    client,
    defaultAssignment,
  ]);

  return {
    value: relatedFields,
    associationMode: currentActivity?.associationMode ?? null,
    activity: currentActivity,
    isFetching,
  };
};

/**
 * I look and feel like a transform function, but I actually dispatch side effects
 * @typedef {import('@kizen/api/activities').Activity} ApiActivity
 * @param {SnakeToCamelCase<ApiActivity>} activity
 */
const getRelatedActivityFieldWithSideEffects = (
  client,
  defaultAssignment = {},
  dispatchAssignment,
  activity,
  authentication
) => {
  const relationshipDefaults = activity?.customObjects || EMPTY_ARRAY;
  const associatedObjects = activity?.associatedObjects || EMPTY_ARRAY;

  const associatedObjectData = associatedObjects.reduce((acc, obj) => {
    acc[obj.customObject.id] = {
      isReadonly: obj.isReadonly,
      isRequired: obj.isRequired,
      order: obj.order,
    };

    return acc;
  }, {});

  return relationshipDefaults
    .reduce((current, model) => {
      const { isReadonly, isRequired, order } =
        associatedObjectData[model.id] ?? {};

      const flds = EMPTY_ARRAY;
      let component;
      if (isContact(model)) {
        const contactSectionAccess = getContactSectionAccess({
          authentication,
        });
        const setterProp = {
          type: ASSIGNMENT_SET,
          name: model.name,
        };
        component = {
          type: CONTACT_SELECTOR,
          setterProp,
          getterProp: model.name,
          isReadonly,
          isRequired,
        };
        dispatchAssignment({
          type: ASSIGNMENT_INIT,
          name: model.name,
        });
        if (client) {
          dispatchAssignment({
            ...setterProp,
            value: {
              value: client.value || client.id,
              label:
                client.label ||
                client.displayName ||
                client.email ||
                client.fullName,
            },
          });
        }
        current = [
          ...current,
          {
            ...model,
            fields: flds,
            component,
            access: {
              edit: contactSectionAccess.view,
              view: contactSectionAccess.view,
            },
            priority: order,
          },
        ];
      } else {
        const customObjectEntitiesAccess = getCustomObjectEntitiesAccessById(
          { authentication },
          model.id
        );
        const setterProp = {
          type: ASSIGNMENT_SET,
          name: model.name,
        };
        component = {
          type: isContact(model) ? CONTACT_SELECTOR : CUSTOM_SELECTOR,
          setterProp,
          getterProp: model.name,
          isReadonly,
          isRequired,
        };
        dispatchAssignment({
          type: ASSIGNMENT_INIT,
          name: model.name,
        });
        // set up default if a match
        Object.keys(defaultAssignment).forEach((key) => {
          if (model.name === key || model.id === key) {
            dispatchAssignment({
              ...setterProp,
              value: defaultAssignment[key],
            });
          }
        });
        current = [
          ...current,
          {
            ...model,
            fields: flds,
            component,
            ...(customObjectEntitiesAccess
              ? {
                  access: {
                    edit: customObjectEntitiesAccess.enabled,
                    view: customObjectEntitiesAccess.enabled,
                  },
                }
              : dummyObject),
            fieldType: FIELD_TYPES.Relationship.type,
            relations: [
              {
                fetchUrl: `field-model-custom`,
                model: model.id,
                modelFetchUrl: model.fetchUrl,
                objectName: model.objectName,
              },
            ],
            priority: order,
          },
        ];
      }
      return current;
    }, [])
    .sort((a, b) =>
      a.priority === b.priority
        ? a.objectName.localeCompare(b.objectName)
        : a.priority - b.priority
    );
};

const useCurrentActivity = (id, queryOptions, apiOptions, errorCallback) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const { data, error, isFetching, isRefetching, isError, refetch } =
    useCamelCaseActivityQuery(id, queryOptions, apiOptions);

  useEffect(() => {
    if (isError) {
      const err = ActivityService.notFoundError(error);

      if (errorCallback && typeof errorCallback === 'function') {
        errorCallback(err);
      } else {
        const orig = getOriginalError(err);
        showToast({
          message:
            orig?.message ||
            t(
              'There was an error loading the {{activityName}}. Please fix and try again.',
              {
                activityName: t('Chosen Activity'),
              }
            ),
          variant: toastVariant.FAILURE,
        });
      }
    }
  }, [isError, error, errorCallback, showToast, t]);

  useEffect(() => {
    id && refetch?.(id);
  }, [id, refetch]);

  return {
    currentActivity: data,
    isFetching: isFetching || isRefetching,
  };
};
